(function () {
  const SEL = "[data-collections-carousel]";
  const slidesMinForLoop = 7;
  /** Visual slide gap (spaceBetween); must match --cc-gap. */
  const CC_GAP = {
    xs: 12,
    md: 24,
    xl: 120,
  };
  /** Must match $duration-cc-slide in section-collections-carousel.scss */
  const CC_SLIDE_SPEED = 1200;
  const CC_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
  /** Fallback for --cc-inactive-scale; the CSS var is the source of truth. */
  const CC_INACTIVE_SCALE = 0.6444;
  /** A running transition may be interrupted after this; earlier requests queue. */
  const CC_INTERRUPT_AFTER = 500;

  /** Wipe = counter-translating slot/inner pair (composited everywhere);
      the reveal edge sweeps while the image stays put. 101% avoids seams. */
  const BG_OFFSET = 101;

  const prefersReducedMotion = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const getCcGap = () => {
    if (typeof window.matchMedia !== "function") return CC_GAP.xs;
    if (window.matchMedia("(min-width: 1320px)").matches) return CC_GAP.xl;
    if (window.matchMedia("(min-width: 750px)").matches) return CC_GAP.md;
    return CC_GAP.xs;
  };

  const isCcMobile = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 749px)").matches;

  const destroy = (root) => {
    if (typeof root._collectionsCarouselCleanup === "function") {
      root._collectionsCarouselCleanup();
      delete root._collectionsCarouselCleanup;
    }
    const swiperEl = root.querySelector(".collections-carousel-swiper");
    if (swiperEl?.swiper) {
      swiperEl.swiper.destroy(true, true);
    }
  };

  const getBgIndexFromSlide = (slideEl, root) => {
    if (!slideEl) return 0;
    const collIdx = parseInt(
      slideEl.getAttribute("data-collection-index") ?? "",
      10
    );
    if (!Number.isNaN(collIdx)) return collIdx;
    const parsed = parseInt(
      slideEl.getAttribute("data-cc-slide-index") ?? "",
      10
    );
    if (!Number.isNaN(parsed)) {
      const blockCount =
        parseInt(root?.getAttribute("data-cc-blocks") ?? "", 10) || 0;
      return blockCount > 0 ? parsed % blockCount : parsed;
    }
    return 0;
  };

  const getActiveBgIndex = (swiper, root) => {
    const slide = swiper.slides?.[swiper.activeIndex];
    if (!slide) return 0;
    return getBgIndexFromSlide(slide, root);
  };

  const bgSlotInner = (slot) =>
    slot?.querySelector(".collections-carousel__bg-slot-inner");

  /** sign 0 = fully shown; ±1 = hidden off that side (inner counter-translated). */
  const setBgSlotOffset = (slot, sign, { animate = false } = {}) => {
    if (!slot) return;
    const inner = bgSlotInner(slot);
    const transition = animate
      ? `transform ${CC_SLIDE_SPEED}ms ${CC_EASING}`
      : "none";
    slot.style.transition = transition;
    slot.style.transform = `translateX(${sign * BG_OFFSET}%)`;
    if (inner) {
      inner.style.transition = transition;
      inner.style.transform = `translateX(${sign * -BG_OFFSET}%)`;
    }
    if (!animate) void slot.offsetWidth;
  };

  const clearBgSlotInlineStyles = (slot) => {
    if (!slot) return;
    slot.style.transition = "";
    slot.style.transform = "";
    slot.style.zIndex = "";
    const inner = bgSlotInner(slot);
    if (inner) {
      inner.style.transition = "";
      inner.style.transform = "";
    }
  };

  /** Pre-loads/decodes a lazy image once — decoding mid-transition reads as a stutter. */
  const warmImage = (img) => {
    if (!(img instanceof HTMLImageElement) || img.dataset.ccWarmed === "true")
      return;
    img.dataset.ccWarmed = "true";
    if (img.loading === "lazy") img.loading = "eager";
    if (typeof img.decode === "function") {
      img.decode().catch(() => {});
    }
  };

  /** A late image must not first-paint mid-transition (decode + texture upload
      starve the animation frames) — keep it hidden until decoded, then reveal
      at idle; the slot's own background colour carries the wipe meanwhile. */
  const stageImageReveal = (img) => {
    if (!(img instanceof HTMLImageElement) || img.dataset.ccStaged === "true")
      return;
    img.dataset.ccStaged = "true";
    if (img.complete && img.naturalWidth > 0) return;
    img.style.opacity = "0";
    const show = () => {
      img.style.transition = "opacity 0.35s ease";
      img.style.opacity = "1";
      // Clear the inline styles — the transition shorthand otherwise overrides
      // the CSS hover-zoom transition on media images
      window.setTimeout(() => {
        img.style.transition = "";
        img.style.opacity = "";
      }, 400);
    };
    const showWhenIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(show, { timeout: 2000 });
      } else {
        window.setTimeout(show, 300);
      }
    };
    img.addEventListener(
      "load",
      () => {
        if (typeof img.decode === "function") {
          img.decode().then(showWhenIdle, showWhenIdle);
        } else {
          showWhenIdle();
        }
      },
      { once: true }
    );
    img.addEventListener("error", showWhenIdle, { once: true });
  };

  const stageLateImages = (root) => {
    root
      .querySelectorAll(
        "img.collections-carousel__bg-slot-img, img.collections-carousel__media-img"
      )
      .forEach(stageImageReveal);
  };

  /** One near-invisible unclipped frame — a fully-clipped slot never rasters,
      so its first wipe pays full-viewport raster+upload mid-animation. */
  const prerasterBgSlot = (slot, img) => {
    if (!slot || slot.dataset.ccPreraster === "true") return;
    const run = () => {
      if (
        slot.dataset.ccPreraster === "true" ||
        slot.classList.contains("is-active")
      )
        return;
      slot.dataset.ccPreraster = "true";
      setBgSlotOffset(slot, 0);
      slot.style.opacity = "0.001";
      slot.style.zIndex = "3";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          slot.style.opacity = "";
          // A wipe may have taken the slot over during the raster frame
          if (slot.style.zIndex === "3") {
            clearBgSlotInlineStyles(slot);
          }
        });
      });
    };
    if (img && !(img.complete && img.naturalWidth > 0)) {
      img.addEventListener(
        "load",
        () => {
          if (typeof img.decode === "function") {
            img.decode().then(run, run);
          } else {
            run();
          }
        },
        { once: true }
      );
      return;
    }
    run();
  };

  const warmNeighborBackgrounds = (root, activeIdx, blockCount) => {
    if (!blockCount || blockCount < 2) return;
    [activeIdx - 1, activeIdx + 1].forEach((i) => {
      const idx = ((i % blockCount) + blockCount) % blockCount;
      const slot = root.querySelector(`[data-bg-index="${idx}"]`);
      if (!slot) return;
      const img = slot.querySelector("img.collections-carousel__bg-slot-img");
      warmImage(img);
      prerasterBgSlot(slot, img);
    });
  };

  const warmNearbyCardImages = (swiper, distance = 2) => {
    const slides = swiper.slides || [];
    for (let d = -distance; d <= distance; d += 1) {
      const slide = slides[swiper.activeIndex + d];
      if (!slide) continue;
      warmImage(slide.querySelector("img.collections-carousel__media-img"));
    }
  };

  /** Detach an in-flight wipe's finalize without snapping the bg. */
  const cancelBgWipeFinalize = (root) => {
    if (typeof root._ccBgWipeCancel === "function") {
      root._ccBgWipeCancel();
    }
  };

  const syncBackgroundInstant = (root, activeIdx) => {
    cancelBgWipeFinalize(root);
    const slots = root.querySelectorAll("[data-bg-index]");
    if (!slots.length) return;

    slots.forEach((slot) => {
      const si = parseInt(slot.getAttribute("data-bg-index") ?? "", 10);
      const isActive = si === activeIdx;
      slot.classList.toggle("is-active", isActive);
      clearBgSlotInlineStyles(slot);
      if (isActive) {
        slot.style.zIndex = "1";
      }
    });
  };

  const animateBackground = (root, fromIdx, toIdx, direction) => {
    const slots = root.querySelectorAll("[data-bg-index]");
    const incoming = root.querySelector(`[data-bg-index="${toIdx}"]`);
    if (!slots.length || !incoming || fromIdx === toIdx) return;

    cancelBgWipeFinalize(root);

    if (prefersReducedMotion()) {
      syncBackgroundInstant(root, toIdx);
      return;
    }

    const sign = direction === "next" ? 1 : -1;

    slots.forEach((slot) => {
      const si = parseInt(slot.getAttribute("data-bg-index") ?? "", 10);
      slot.classList.remove("is-active");
      if (si === toIdx) return;
      if (si === fromIdx) {
        setBgSlotOffset(slot, 0);
        slot.style.zIndex = "1";
        return;
      }
      setBgSlotOffset(slot, sign);
      slot.style.zIndex = "0";
    });

    setBgSlotOffset(incoming, sign);
    incoming.style.zIndex = "2";
    void incoming.offsetWidth;
    setBgSlotOffset(incoming, 0, { animate: true });

    let finished = false;
    let timeoutId = null;
    const teardown = () => {
      finished = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      incoming.removeEventListener("transitionend", onEnd);
      if (root._ccBgWipeCancel === teardown) {
        delete root._ccBgWipeCancel;
      }
    };

    const finalize = () => {
      if (finished) return;
      teardown();
      syncBackgroundInstant(root, toIdx);
    };

    const onEnd = (event) => {
      if (event.target !== incoming || event.propertyName !== "transform")
        return;
      finalize();
    };

    incoming.addEventListener("transitionend", onEnd);
    timeoutId = window.setTimeout(finalize, CC_SLIDE_SPEED + 80);
    root._ccBgWipeCancel = teardown;
  };

  const getSlideDirection = (swiper, fromIdx, toIdx, blockCount) => {
    if (swiper.swipeDirection === "prev" || swiper.swipeDirection === "next") {
      return swiper.swipeDirection;
    }
    if (fromIdx === toIdx || blockCount <= 1) return "next";
    const forward = (toIdx - fromIdx + blockCount) % blockCount;
    const backward = (fromIdx - toIdx + blockCount) % blockCount;
    return forward <= backward ? "next" : "prev";
  };

  const getSlideTargetIndex = (sw, slideEl) => {
    if (sw.params.loop && typeof sw.slideToLoop === "function") {
      let ri = parseInt(slideEl.getAttribute("data-swiper-slide-index"), 10);
      if (Number.isNaN(ri) && slideEl.dataset?.swiperSlideIndex != null) {
        ri = parseInt(slideEl.dataset.swiperSlideIndex, 10);
      }
      if (Number.isNaN(ri)) {
        ri = parseInt(slideEl.getAttribute("data-cc-slide-index"), 10);
      }
      if (Number.isNaN(ri)) {
        ri = parseInt(slideEl.getAttribute("data-collection-index"), 10);
      }
      if (!Number.isNaN(ri)) return { type: "loop", index: ri };
    }

    const slideIndex = Array.prototype.indexOf.call(sw.slides, slideEl);
    if (slideIndex >= 0) return { type: "index", index: slideIndex };

    const ci = parseInt(slideEl.getAttribute("data-cc-slide-index"), 10);
    if (!Number.isNaN(ci)) return { type: "index", index: ci };

    return null;
  };

  const goToSlide = (sw, slideEl) => {
    const target = getSlideTargetIndex(sw, slideEl);
    if (!target) return;

    if (target.type === "loop") {
      sw.slideToLoop(target.index);
      return;
    }

    sw.slideTo(target.index);
  };

  const resetSlideTitles = (swiper) => {
    swiper.slides.forEach((slide) => {
      // Stray hover state must not enter with the card
      slide.classList.remove("hover-active", "hover-inactive");
      const row = slide.querySelector(".collections-carousel__title-row");
      if (!row) return;
      row.classList.remove("is-title-entering", "is-title-visible");
    });
  };

  const enterActiveSlideTitle = (swiper) => {
    const slide = swiper.slides?.[swiper.activeIndex];
    const row = slide?.querySelector(".collections-carousel__title-row");
    if (!row || !slide.classList.contains("swiper-slide-active")) return;

    if (prefersReducedMotion()) {
      row.classList.add("is-title-visible");
      return;
    }

    row.classList.add("is-title-entering");

    const onEnd = (event) => {
      if (event.target !== row || event.animationName !== "cc-title-enter")
        return;
      row.classList.remove("is-title-entering");
      row.classList.add("is-title-visible");
      row.removeEventListener("animationend", onEnd);
    };

    row.addEventListener("animationend", onEnd);
  };

  const playActiveSlideTitleEnter = (swiper) => {
    if (!swiper?.slides?.length) return;
    resetSlideTitles(swiper);
    requestAnimationFrame(() => {
      enterActiveSlideTitle(swiper);
    });
  };

  const playSlideTitleEnter = (slideEl) => {
    const row = slideEl?.querySelector(".collections-carousel__title-row");
    if (!row) return;

    row.classList.remove("is-title-entering", "is-title-visible");

    if (prefersReducedMotion()) {
      row.classList.add("is-title-visible");
      return;
    }

    requestAnimationFrame(() => {
      row.classList.add("is-title-entering");

      const onEnd = (event) => {
        if (event.target !== row || event.animationName !== "cc-title-enter")
          return;
        row.classList.remove("is-title-entering");
        row.classList.add("is-title-visible");
        row.removeEventListener("animationend", onEnd);
      };

      row.addEventListener("animationend", onEnd);
    });
  };

  /* The stage itself is the (host) cursor-arrow zone: the pill works across the whole rail and
     is suppressed over the cards, whose own click/hover handling stays first. Touch devices get
     no wiring at all — the stage is real content, so a tap must not page the rail. */
  const initEdgeCursor = (stageEl, swiper, navigate) => {
    if (
      !stageEl ||
      !swiper ||
      typeof window.initCursorArrowZone !== "function" ||
      !stageEl.classList.contains("cursor-arrow-zone")
    ) {
      return () => {};
    }

    const canUseEdgeCursor =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (!canUseEdgeCursor) {
      return () => {};
    }

    const overCard = (event) =>
      event?.target instanceof Element &&
      event.target.closest(".collections-carousel__card") != null;

    const nav = window.initCursorArrowZone(stageEl, {
      onNavigate: (direction, event) => {
        if (overCard(event)) return;
        navigate(direction);
      },
      isSuppressed: (clientX, clientY, event) => overCard(event),
      isEndReached: (direction) => {
        if (swiper.params.loop || swiper.params.rewind) return false;
        return direction === "prev" ? swiper.isBeginning : swiper.isEnd;
      },
    });

    swiper.on("slideChange", nav.sync);

    return nav.destroy;
  };

  const initSingleSlide = (root, swiperEl, slideEl) => {
    root.classList.add("collections-carousel--single");
    slideEl?.classList.add("swiper-slide-active");
    syncBackgroundInstant(root, getBgIndexFromSlide(slideEl, root));

    const card = slideEl?.querySelector(".collections-carousel__card");
    if (card) {
      card.removeAttribute("inert");
      if (card.tagName === "A") {
        card.removeAttribute("tabindex");
      } else {
        card.tabIndex = 0;
      }
    }

    if (typeof window.initArrowMoveOnHover === "function") {
      window.initArrowMoveOnHover(swiperEl, ".swiper-slide");
    }

    requestAnimationFrame(() => {
      playSlideTitleEnter(slideEl);
    });

    root._collectionsCarouselCleanup = () => {
      root.classList.remove("collections-carousel--single");
      slideEl?.classList.remove("swiper-slide-active");
    };

    root.dataset.collectionsCarouselInit = "true";
    root._collectionsCarouselDestroy = () => destroy(root);
  };

  const init = (section) => {
    const root = section.querySelector?.(SEL);
    if (!root) return;

    const swiperEl = root.querySelector(".collections-carousel-swiper");
    const slideEls = swiperEl?.querySelectorAll(".swiper-slide") || [];

    if (!swiperEl || slideEls.length === 0) return;

    destroy(root);
    stageLateImages(root);

    const blockCount = parseInt(root.getAttribute("data-cc-blocks") ?? "", 10);
    if (blockCount === 1) {
      initSingleSlide(root, swiperEl, slideEls[0]);
      return;
    }

    const count = slideEls.length;
    const wantsLoop = count >= slidesMinForLoop;
    const rtl = document.documentElement.getAttribute("dir") === "rtl";
    const touchEnabled = isCcMobile();
    /** Slide the SCSS pre-init fallback centers (--cc-center-index). */
    const fallbackCenterIndex =
      blockCount > 1 && blockCount < slidesMinForLoop ? blockCount : 0;

    root.classList.toggle("collections-carousel--touch", touchEnabled);

    const readInactiveScale = () => {
      const raw = parseFloat(
        getComputedStyle(root).getPropertyValue("--cc-inactive-scale")
      );
      return raw > 0 && raw < 1 ? raw : CC_INACTIVE_SCALE;
    };
    let inactiveScale = readInactiveScale();

    /** Transform-only active/inactive look from slide.progress; translateX
        compensates the scale so the visual gap stays equal to spaceBetween. */
    const applySlideTransforms = (sw) => {
      const slides = sw.slides || [];
      // slide.progress is positive for slides BEFORE the active one, negative after.
      const sign = sw.rtlTranslate ? -1 : 1;
      const shrink = 1 - inactiveScale;
      for (let i = 0; i < slides.length; i += 1) {
        const slideEl = slides[i];
        const progress =
          typeof slideEl.progress === "number" ? slideEl.progress : 0;
        const absP = Math.abs(progress);
        const near = Math.min(absP, 1);
        const size = slideEl.swiperSlideSize || slideEl.offsetWidth || 0;
        const shift = size * shrink * (near / 2 + Math.max(absP - 1, 0));
        const scale = 1 - shrink * near;
        slideEl.style.transform = `translateX(${(
          sign *
          Math.sign(progress) *
          shift
        ).toFixed(2)}px) scale(${scale.toFixed(4)})`;
      }
    };

    /** Mirrors Swiper's transition duration onto the slides. */
    const applySlideTransition = (sw, duration) => {
      const ms = prefersReducedMotion() ? 0 : duration || 0;
      (sw.slides || []).forEach((slideEl) => {
        slideEl.style.transitionDuration = `${ms}ms`;
      });
    };

    const swiper = new Swiper(swiperEl, {
      rtl,
      loop: wantsLoop,
      ...(wantsLoop
        ? {
            loopPreventsSliding: false,
            /* Extra buffer — the transforms pull cards inward, more are visible */
            loopAdditionalSlides: 2,
          }
        : {}),
      initialSlide: fallbackCenterIndex,
      speed: prefersReducedMotion() ? 0 : CC_SLIDE_SPEED,
      slidesPerView: "auto",
      centeredSlides: true,
      centerInsufficientSlides: true,
      centeredSlidesBounds: false,
      spaceBetween: getCcGap(),
      slideToClickedSlide: false,
      allowTouchMove: touchEnabled,
      simulateTouch: false,
      grabCursor: false,
      touchEventsTarget: "container",
      touchStartPreventDefault: false,
      watchSlidesProgress: true,
      threshold: 4,
      resizeObserver: true,
      // Rounded grid vs fractional card widths landed ~5px off-center (active
      // slide rested at scale 0.9968)
      roundLengths: false,
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        progress: applySlideTransforms,
        setTransition: applySlideTransition,
      },
    });

    /* CSS-fallback handoff — the constructor already applied the transforms synchronously */
    slideEls.forEach((el) => el.classList.remove("cc-fallback-active"));

    /* Commit loopFix's DOM moves + snap before the animated write, or the transition
       starts from a stale baseline (Chrome: moved slides flash full-size on the first
       navigation; Firefox: backwards fling). Swiper's own reflow hack covers only
       slideNext/slidePrev, not the slideToLoop path. */
    swiper.on("beforeLoopFix", () => {
      applySlideTransition(swiper, 0);
    });
    swiper.on("loopFix", () => {
      void swiperEl.clientLeft;
    });

    /* The section sizes itself off the viewport, so it resizes on things the rail
       does not care about — the mobile browser chrome retracting, for one. Only
       a changed rail or card width needs update(): it re-applies the slide
       classes, which replays the active state. Card width tracks height on short
       viewports (--cc-card-w is capped by --cc-card-h-limit), so it is measured
       too. A mid-transition update also cuts the animation — that one waits. */
    swiper.off("resize");
    let resizeDeferred = false;
    const measureRail = () =>
      `${swiperEl.clientWidth}x${swiper.slides?.[0]?.offsetWidth || 0}`;
    let lastRailSize = measureRail();
    const requestCcUpdate = (force) => {
      if (swiper.destroyed) return;
      const size = measureRail();
      if (!force && size === lastRailSize) return;
      lastRailSize = size;
      if (swiper.animating) {
        resizeDeferred = true;
        return;
      }
      swiper.update();
    };
    swiper.on("resize", () => requestCcUpdate());
    swiper.on("transitionEnd", () => {
      swiper.slides.forEach((slide) => {
        slide.classList.remove("hover-active", "hover-inactive");
      });
      root.classList.remove("collections-carousel--transitioning");
      if (!resizeDeferred) return;
      resizeDeferred = false;
      swiper.update();
    });

    let lastBgIndex = getActiveBgIndex(swiper, root);
    syncBackgroundInstant(root, lastBgIndex);

    /* Warm the images the first navigations will need, off the critical path */
    let cancelImageWarmup = () => {};
    {
      const warmAroundCurrent = () => {
        warmNeighborBackgrounds(root, lastBgIndex, blockCount);
        // ALL cards: rail-edge cards decoding mid-transition cost paint frames
        warmNearbyCardImages(swiper, slideEls.length);
      };
      if (typeof window.requestIdleCallback === "function") {
        const idleId = window.requestIdleCallback(warmAroundCurrent, {
          timeout: 600,
        });
        cancelImageWarmup = () => window.cancelIdleCallback(idleId);
      } else {
        const timerId = window.setTimeout(warmAroundCurrent, 400);
        cancelImageWarmup = () => window.clearTimeout(timerId);
      }
    }

    let removeCcMqListeners = () => {};
    if (typeof window.matchMedia === "function") {
      const applyCcMedia = () => {
        inactiveScale = readInactiveScale();
        const next = getCcGap();
        if (swiper.params.spaceBetween !== next) {
          swiper.params.spaceBetween = next;
        }
        // Gap and scale changed under the same width — force past the size guard
        requestCcUpdate(true);
      };

      const applyTouchMode = () => {
        const mobile = isCcMobile();
        swiper.params.allowTouchMove = mobile;
        swiper.allowTouchMove = mobile;
        root.classList.toggle("collections-carousel--touch", mobile);
      };

      const m1320 = window.matchMedia("(min-width: 1320px)");
      const m750 = window.matchMedia("(min-width: 750px)");
      m1320.addEventListener("change", applyCcMedia);
      m750.addEventListener("change", applyCcMedia);
      m750.addEventListener("change", applyTouchMode);
      removeCcMqListeners = () => {
        m1320.removeEventListener("change", applyCcMedia);
        m750.removeEventListener("change", applyCcMedia);
        m750.removeEventListener("change", applyTouchMode);
      };
    }

    requestAnimationFrame(() => {
      playActiveSlideTitleEnter(swiper);
    });

    const updateCardLinkAccessibility = () => {
      swiper.slides.forEach((slide) => {
        const card = slide.querySelector(".collections-carousel__card");
        if (!card) return;

        card.removeAttribute("inert");

        if (card.tagName === "A") {
          card.removeAttribute("tabindex");
          return;
        }

        if (slide.classList.contains("swiper-slide-active")) {
          card.tabIndex = -1;
        } else {
          card.tabIndex = 0;
        }
      });
    };

    /** A navigation request is never dropped: it interrupts after
        CC_INTERRUPT_AFTER or queues until that window closes. */
    let transitionStartedAt = 0;
    let pendingNav = null;
    let pendingNavTimer = null;

    const clearPendingNav = () => {
      pendingNav = null;
      if (pendingNavTimer != null) {
        window.clearTimeout(pendingNavTimer);
        pendingNavTimer = null;
      }
    };

    const runOrQueueNav = (action) => {
      const lockLeft =
        transitionStartedAt + CC_INTERRUPT_AFTER - performance.now();
      if (!swiper.animating || lockLeft <= 0) {
        clearPendingNav();
        action();
        return;
      }
      pendingNav = action;
      if (pendingNavTimer != null) window.clearTimeout(pendingNavTimer);
      pendingNavTimer = window.setTimeout(() => {
        pendingNavTimer = null;
        const queued = pendingNav;
        pendingNav = null;
        if (queued && !swiper.destroyed) queued();
      }, Math.max(0, lockLeft));
    };

    let pointerActivatedCard = null;

    const onCardPointerDown = (evt) => {
      pointerActivatedCard = evt.target.closest(".collections-carousel__card");
    };

    const onCardActivate = (evt) => {
      if (isCcMobile()) return;

      const card = evt.target.closest(".collections-carousel__card");
      if (!card || !swiperEl.contains(card)) return;

      const slideEl = card.closest(".swiper-slide");
      if (!slideEl || slideEl.classList.contains("swiper-slide-active")) {
        return;
      }

      /* A non-active card click selects, never navigates the link */
      if (card.tagName === "A") {
        evt.preventDefault();
      }

      runOrQueueNav(() => goToSlide(swiper, slideEl));
    };

    const onCardFocus = (evt) => {
      if (isCcMobile()) return;

      const card = evt.target.closest(".collections-carousel__card");
      if (!card || !swiperEl.contains(card)) return;

      if (pointerActivatedCard === card) {
        pointerActivatedCard = null;
        return;
      }

      const slideEl = card.closest(".swiper-slide");
      if (
        !slideEl ||
        slideEl.classList.contains("swiper-slide-active") ||
        swiper.animating
      ) {
        return;
      }

      goToSlide(swiper, slideEl);
    };

    const onCardKeydown = (evt) => {
      if (isCcMobile()) return;

      if (evt.key !== "Enter" && evt.key !== " ") return;

      const card = evt.target.closest(".collections-carousel__card");
      if (!card || card.tagName === "A" || !swiperEl.contains(card)) return;

      const slideEl = card.closest(".swiper-slide");
      if (!slideEl || slideEl.classList.contains("swiper-slide-active")) {
        return;
      }

      evt.preventDefault();
      runOrQueueNav(() => goToSlide(swiper, slideEl));
    };

    swiperEl.addEventListener("pointerdown", onCardPointerDown, true);
    swiperEl.addEventListener("click", onCardActivate);
    swiperEl.addEventListener("focusin", onCardFocus);
    swiperEl.addEventListener("keydown", onCardKeydown);

    /** Edge-cursor direction; wins over the index-delta guess for the bg wipe. */
    let pendingBgDirection = null;

    /* Routed through the neighbor slide, not slideNext/slidePrev: those loopFix
       by the layout-measured window, and the transforms keep more cards on
       screen than that — a relocated slide then teleports mid-move. Clicking a
       card takes the slideToLoop path, which never shows it. */
    const navigateCarousel = (dir) => {
      runOrQueueNav(() => {
        pendingBgDirection = dir;
        const step = dir === "next" ? 1 : -1;
        const neighbor = swiper.slides?.[swiper.activeIndex + step];
        if (neighbor) {
          goToSlide(swiper, neighbor);
          return;
        }
        if (dir === "next") {
          swiper.slideNext();
        } else {
          swiper.slidePrev();
        }
      });
    };

    swiper.on("slideChangeTransitionStart", () => {
      transitionStartedAt = performance.now();
      // Hover-arrow rules are inert while this class is on (the card slides out
      // from under the pointer and enter/leave churn replays the arrow)
      root.classList.add("collections-carousel--transitioning");

      // The enter animation carries the reveal delay (CSS)
      if (swiper.activeIndex !== swiper.previousIndex) {
        playActiveSlideTitleEnter(swiper);
      }

      const nextIdx = getActiveBgIndex(swiper, root);
      if (nextIdx !== lastBgIndex) {
        const direction =
          pendingBgDirection ||
          getSlideDirection(swiper, lastBgIndex, nextIdx, blockCount);
        animateBackground(root, lastBgIndex, nextIdx, direction);
        lastBgIndex = nextIdx;
      }
      pendingBgDirection = null;
    });

    swiper.on("slideChangeTransitionEnd", () => {
      updateCardLinkAccessibility();
      // Warm AFTER the transition — kicking decode of the neighbor images
      // (two 2800px backgrounds) at transition start starved the animation
      // frames on every first activation of a slide.
      warmNeighborBackgrounds(root, lastBgIndex, blockCount);
      warmNearbyCardImages(swiper);
    });

    updateCardLinkAccessibility();

    if (swiperEl && typeof window.initArrowMoveOnHover === "function") {
      window.initArrowMoveOnHover(swiperEl, ".swiper-slide");
    }

    const stageEl = root.querySelector(".collections-carousel__stage");
    const removeEdgeCursorListeners = initEdgeCursor(
      stageEl,
      swiper,
      navigateCarousel
    );

    root._collectionsCarouselCleanup = () => {
      clearPendingNav();
      cancelImageWarmup();
      cancelBgWipeFinalize(root);
      root.classList.remove("collections-carousel--touch");
      root.classList.remove("collections-carousel--transitioning");
      removeEdgeCursorListeners();
      removeCcMqListeners();
      swiperEl.removeEventListener("pointerdown", onCardPointerDown, true);
      swiperEl.removeEventListener("click", onCardActivate);
      swiperEl.removeEventListener("focusin", onCardFocus);
      swiperEl.removeEventListener("keydown", onCardKeydown);
    };

    root.dataset.collectionsCarouselInit = "true";
    root._collectionsCarouselDestroy = () => destroy(root);
  };

  const section = document.currentScript?.parentElement;
  if (section) init(section);

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector?.(SEL);
    if (
      root?.dataset.collectionsCarouselInit === "true" &&
      typeof root._collectionsCarouselDestroy === "function"
    ) {
      root._collectionsCarouselDestroy();
      delete root.dataset.collectionsCarouselInit;
      delete root._collectionsCarouselDestroy;
    }
  });
})();
